import React, { useState } from 'react';
import SectionShape from '../../../components/SectionShape';

/**
 * Ask section (#ask) — natural-language querying of purchase history.
 * Clicking a question chip swaps the answer panel. Ported from the
 * design's DCLogic `_ask(i)` — now clean React state (activeQ index).
 *
 * Each answer's figures are illustrative (from the design), showing the
 * kind of item-level answer KACHNG returns from real receipts.
 */

const mono = "'JetBrains Mono', ui-monospace, monospace";

const QUESTIONS = [
  {
    q: '"How much were the paper towels at Target?"',
    lead: '$24.99',
    link: 'View receipt →',
    lines: [
      'Bounty Select-A-Size, 12 rolls',
      'Target · Culver City',
      'June 14, 2026 · 2:47 PM',
      'Receipt total $87.43 · line item 7 of 23',
    ],
  },
  {
    q: '"How much did I spend on coffee last month?"',
    lead: '$47.30',
    link: 'View all receipts →',
    lines: [
      '11 receipts · June 2026',
      'Blue Bottle · $31.75 across 6 visits',
      'Verve · $9.80 · Alfred · $5.75',
      'Jun 1 – Jun 30',
    ],
  },
  {
    q: '"When did I buy the Uniqlo jacket?"',
    lead: 'Mar 14',
    link: 'View receipt →',
    lines: [
      'Uniqlo Blocktech Parka · $89.90',
      'Uniqlo · Santa Monica',
      'Return window closed Apr 13',
      'Order #48213 · March 14, 2026 · 4:12 PM',
    ],
  },
  {
    q: '"What\u2019s my Whole Foods total this year?"',
    lead: '$2,214.85',
    link: 'View all receipts →',
    lines: [
      '31 trips · Jan 1 – Jul 19',
      'Average $71.45 per visit',
      '42% of total grocery spend',
      'From 31 receipts · 847 line items',
    ],
  },
];

function QuestionChip({ text, active, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" className="question-chip" aria-pressed={active}
      onClick={onClick}
      data-rv="up"
      style={{
        fontFamily: 'inherit',
        textAlign: 'left',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        border: `1px solid ${
          active ? 'rgba(0,230,118,0.55)' : hover ? '#3A403A' : 'var(--border-soft)'
        }`,
        borderRadius: '999px',
        padding: '18px 26px',
        fontSize: 'clamp(15px,1.4vw,17px)',
        color: active ? 'var(--kachng-green)' : 'var(--text)',
        background: active ? 'rgba(0,230,118,0.07)' : '#161917',
        transition: 'border-color 0.25s, color 0.25s, background 0.25s',
        cursor: 'pointer',
        userSelect: 'none',
        minHeight: '44px',
        boxSizing: 'border-box',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span>{text}</span>
      <span
        style={{
          color: active ? 'var(--kachng-green)' : 'var(--text-dim)',
          flex: 'none',
          fontWeight: 700,
        }}
      >
        ↗
      </span>
    </button>
  );
}

export default function Ask({ mobile = false }) {
  const [activeQ, setActiveQ] = useState(0);
  const selected = mobile && activeQ > 1 ? 0 : activeQ;
  const a = QUESTIONS[selected];

  return (
    <section id="ask" style={{ position: 'relative' }}>
      {/* Background resolving wave — chaos on the left, a clear answer on the right */}
      {!mobile && <SectionShape kind="resolve" opacity={0.3} />}

      <div className="mobile-section-inner"
        style={{
          position: 'relative',
          zIndex: 1,
          padding: 'clamp(56px,7vw,88px) clamp(20px,5vw,56px)',
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div data-rv="up" style={{ marginBottom: 'clamp(12px,1.5vw,20px)' }}>
          <div
            style={{
              fontFamily: mono,
              color: 'var(--kachng-green)',
              fontSize: '12px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 500,
              marginBottom: '24px',
            }}
          >
            Ask your spending
          </div>
          <h2
            style={{
              margin: '0 0 20px',
              fontSize: 'clamp(34px,4.4vw,56px)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.02,
              textWrap: 'balance',
            }}
          >
            Your purchases already know the answer.
          </h2>
          <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.6, color: 'var(--text-300)' }}>
            Ask in plain English. Get answers from the things you actually bought.&nbsp;
          </p>
        </div>

        <div className="ask-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
            gap: 'clamp(40px,5vw,64px)',
            alignItems: 'center',
          }}
        >
          {/* Question chips */}
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {(mobile ? QUESTIONS.slice(0, 2) : QUESTIONS).map((item, i) => (
                <QuestionChip
                  key={i}
                  text={item.q}
                  active={selected === i}
                  onClick={() => setActiveQ(i)}
                />
              ))}
            </div>
          </div>

          {/* Answer panel */}
          <div data-rv="scale" style={{ position: 'relative' }}>
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '-1px',
                borderRadius: '25px',
                background:
                  'radial-gradient(60% 50% at 50% 0%, rgba(0,230,118,0.35) 0%, rgba(0,230,118,0.06) 55%, transparent 100%)',
                width: 'min(402px,100%)',
                margin: '0 auto',
                left: 0,
                right: 0,
                pointerEvents: 'none',
              }}
            />
            <div className="ask-answer" aria-live="polite"
              style={{
                background:
                  'radial-gradient(120% 90% at 50% 0%, var(--card-grad-2-top) 0%, var(--card-grad-bottom) 65%)',
                border: '1px solid var(--border-soft)',
                borderRadius: '24px',
                padding: '28px',
                width: 'min(400px,100%)',
                boxSizing: 'border-box',
                margin: '0 auto',
                boxShadow:
                  '0 24px 64px rgba(0,0,0,0.55), 0 0 80px rgba(0,230,118,0.07), inset 0 1px 0 rgba(0,230,118,0.14)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '420px',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  data-wave=""
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 35% 30%, #1E5A38 0%, #123B26 70%)',
                    border: '1px solid rgba(0,230,118,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    flex: 'none',
                  }}
                >
                  {[
                    { h: 12, d: 0 },
                    { h: 22, d: 0.15 },
                    { h: 16, d: 0.3 },
                    { h: 24, d: 0.45 },
                    { h: 13, d: 0.6 },
                  ].map((bar, i) => (
                    <span
                      key={i}
                      style={{
                        width: '3px',
                        height: `${bar.h}px`,
                        borderRadius: '2px',
                        background: 'var(--kachng-green)',
                        animation: `aiWave 1.1s ease-in-out infinite`,
                        animationDelay: `${bar.d}s`,
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <div
                    style={{
                      fontFamily: mono,
                      color: 'var(--kachng-green)',
                      fontSize: '15px',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    KACHNG ANSWER
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12.5px', letterSpacing: '0.02em' }}>
                    From your actual purchase history.
                  </div>
                </div>
              </div>

              <div style={{ margin: '22px 0 0', fontSize: '15px', color: 'var(--text-muted)', fontStyle: 'italic', minHeight: '44px' }}>
                {a.q}
              </div>

              <div
                style={{
                  marginTop: '16px',
                  fontFamily: mono,
                  fontSize: 'clamp(34px,3.2vw,44px)',
                  fontWeight: 700,
                  letterSpacing: '-0.04em',
                  color: 'var(--kachng-green)',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}
              >
                {a.lead}
              </div>

              <div style={{ borderTop: '1px solid var(--border-hairline)', margin: '20px 0 18px' }} />

              <div
                style={{
                  borderLeft: '2px solid rgba(0,230,118,0.28)',
                  paddingLeft: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '9px',
                }}
              >
                {(mobile ? a.lines.slice(0, 2) : a.lines).map((text, i) => (
                  <div
                    key={i}
                    style={{
                      fontFamily: mono,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.5,
                      fontSize: i === 0 ? '15px' : '13px',
                      color: i === 0 ? 'var(--text)' : 'var(--text-muted)',
                      fontWeight: i === 0 ? 600 : 400,
                    }}
                  >
                    {text}
                  </div>
                ))}
              </div>

              <div className="ask-answer-link" style={{ marginTop: 'auto', paddingTop: '20px' }}>
                <AnswerLink text={a.link} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AnswerLink({ text }) {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: hover ? 'var(--kachng-green)' : 'rgba(0,230,118,0.6)',
        cursor: 'pointer',
        letterSpacing: '0.01em',
        transition: 'color 0.2s',
      }}
    >
      {text}
    </span>
  );
}